import React, { ReactNode, useEffect, useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { LoadingIndicator } from '../core-ui';
import DataStore from '../support/DataStore';
import createContext from '../support/helpers/createContext';
import { Account } from '../types/shared/Account';
import { User } from '../types/shared/User';
import { fetchJson } from '../support/useFetch';

import { AUTH_TOKEN_KEY } from './AuthTokenValidator';
import LandingPage from './LandingPage';

type Context = {
  account: Account;
  user: User;
  refresh: () => void;
};

const [useContext, Provider] = createContext<Context>();

const dataStore = DataStore.getInstance('app');

type AuthResult =
  | {
      success: true;
      account: Account;
      user: User;
      token: string;
      system_admin: boolean;
    }
  | { success: false };

async function getAuthDetails(
  authToken: string | null,
  signal: AbortSignal | undefined,
): Promise<AuthResult> {
  if (!authToken) {
    return { success: false };
  }
  return await fetchJson('/api/auth/refresh', {
    headers: { 'X-Auth-Token': authToken },
    signal,
  });
}

export function AuthStateProvider(props: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // Get initial token only at mount
  const [initialAuthToken] = useState(() =>
    sessionStorage.getItem(AUTH_TOKEN_KEY),
  );
  const authTokenRef = useRef(initialAuthToken);

  const { isLoading, error, refetch } = useQuery(
    ['auth-account'],
    ({ signal }) => getAuthDetails(authTokenRef.current, signal),
    {
      onSuccess: (data) => {
        if (data.success) {
          dataStore.set('user', data.user);
          dataStore.set('account', {
            ...data.account,
            token: data.token,
            system_admin: data.system_admin,
          });
        } else {
          dataStore.set('user', undefined);
          dataStore.set('account', undefined);
        }
      },
    },
  );

  // The logic in this effect syncs changes from dataStore to state.
  // TODO: If we move all call-sites to useAuth() then we can instead mirror
  // from state to dataStore and eventually get rid of dataStore.
  useEffect(() => {
    const accountSubscription = dataStore.onChange('account', (newAccount) => {
      if (newAccount) {
        const authToken = newAccount.token ?? '';
        sessionStorage.setItem(AUTH_TOKEN_KEY, authToken);
        authTokenRef.current = authToken;
      } else {
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        authTokenRef.current = null;
      }
      setAccount(newAccount ?? null);
    });
    // TODO: Instead of tracking account and user separately use a tagged union.
    const userSubscription = dataStore.onChange('user', (newUser) => {
      setUser(newUser ?? null);
    });
    const subscriptions = [accountSubscription, userSubscription];

    return () => {
      for (const subscription of subscriptions) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const context = useMemo<Context | null>(() => {
    return account && user ? { account, user, refresh: refetch } : null;
  }, [account, user, refetch]);

  if (error) {
    return <p>{String(error)}</p>;
  }

  if (!context && isLoading) {
    return <LoadingIndicator />;
  }

  if (!context) {
    return <LandingPage />;
  }

  return <Provider value={context}>{props.children}</Provider>;
}

export function useAuthState() {
  return useContext();
}
